import _ from 'lodash';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import type { ColumnRuleInputWithUIMetadata } from '@/tableDefinitionEditor/columnRules/columnRule.constants';
import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';
import type { ColumnDefinitionInputV1 } from '@seeqdev/api/model/ColumnDefinitionInputV1';
import type { ColumnDefinitionOutputV1 } from '@seeqdev/api/model/ColumnDefinitionOutputV1';
import type { ColumnRuleInputV1 } from '@seeqdev/api/model/ColumnRuleInputV1';
import type { ColumnRuleOutputV1 } from '@seeqdev/api/model/ColumnRuleOutputV1';
import type { ModelApplicationMappingV1 } from '@seeqdev/api/model/ModelApplicationMappingV1';
import type { ModelTableApplicationsOutputListV1 } from '@seeqdev/api/model/ModelTableApplicationsOutputListV1';
import type { ModelTableApplicationsOutputV1 } from '@seeqdev/api/model/ModelTableApplicationsOutputV1';

export const getRuleAsOutput = (
  columnRule: ColumnRuleInputWithUIMetadata,
  columnAsInput?: ColumnDefinitionInputV1,
  columnAsOutput?: ColumnDefinitionOutputV1,
): ColumnRuleOutputV1 | undefined => {
  const existingRulesAsInputs = columnAsInput?.columnRules ?? [];
  const existingRulesAsOutputs = columnAsOutput?.rules ?? [];
  const ruleWithoutUIProps: ColumnRuleInputV1 = _.omit(columnRule, 'isCollapsed');
  const indexOfMatch = existingRulesAsInputs.findIndex((rule) => _.isEqual(rule, ruleWithoutUIProps));
  return indexOfMatch >= 0 ? existingRulesAsOutputs[indexOfMatch] : undefined;
};

/**
 * Ensures that item-creating rules have a valid name by defaulting to the column name if the rule's name is empty
 * or whitespace-only.
 *
 * Supported rule types: formulaCreator, scalarCreator.
 *
 * @param columnRule - The column rule to validate.
 * @param columnName - The column's display name to use as a fallback (must be non-empty)
 * @returns A new column rule with name populated if needed.
 */
export const ensureRuleHasName = (columnRule: ColumnRuleInputV1, columnName: string): ColumnRuleInputV1 => {
  const trimmedName = columnName.trim();

  if (columnRule.formulaCreator) {
    const rule = columnRule.formulaCreator;
    if (
      (rule.nameBinding?.value && rule.nameBinding.value.trim() !== '') ||
      rule.nameBinding?.columnIndex !== undefined
    ) {
      return columnRule;
    }
    return {
      ...columnRule,
      [SeeqNames.MaterializedTables.Rules.FormulaCreator]: { ...rule, nameBinding: { value: trimmedName } },
    };
  }

  if (columnRule.scalarCreator) {
    const rule = columnRule.scalarCreator;
    if (rule.name && rule.name.trim() !== '') {
      return columnRule;
    }
    return {
      ...columnRule,
      [SeeqNames.MaterializedTables.Rules.ScalarCreator.BaseScalarCreator]: { ...rule, name: trimmedName },
    };
  }

  return columnRule;
};

export const getColumnsFromModelApplications = (
  applications: ModelTableApplicationsOutputListV1,
  allColumns: ColumnDefinition[],
  inputFeatureColumnIds: string[] = [],
): ColumnDefinition[] => {
  const columnIds = new Set(
    (applications?.applications ?? [])
      .flatMap((app: ModelTableApplicationsOutputV1) => app.mappings ?? [])
      .map((mapping: ModelApplicationMappingV1) => mapping.columnId)
      .filter((columnId): columnId is string => typeof columnId === 'string' && columnId.length > 0),
  );

  return allColumns.filter((column) => columnIds.has(column.id) && !inputFeatureColumnIds.includes(column.id));
};
